import * as React from 'react';
import { cva } from 'class-variance-authority';

import { cn } from './cn';
import { Text } from './Typography';

// Small label chip. Single primitive for both identity (tags, owners,
// drafts) and state (online/offline, pending/done). The tone vocabulary
// covers both:
//
//   neutral / primary / secondary  — identity / category labels
//   info / success / warning / destructive  — state communication
//
// Each tone has one canonical paint. Brand tones are solid; status tones
// are tinted-surface; neutral is bordered with a transparent fill (the
// most flexible default — adapts to whatever surface it sits on).
//
// `outline` is not a tone — Badge is always bordered. Earlier variants
// like `outline` and the muted-bg `neutral` are gone; pick the tone that
// expresses what the chip means.

const badgeVariants = cva(
    'tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-font-semibold tw-transition-colors',
    {
        variants: {
            size: {
                small: 'tw-px-2 tw-py-0',
                medium: 'tw-px-2.5 tw-py-0.5'
            },
            tone: {
                neutral: 'tw-border-border tw-bg-background tw-text-foreground',
                primary: 'tw-border-primary tw-bg-primary tw-text-primary-foreground',
                secondary: 'tw-border-secondary tw-bg-secondary tw-text-secondary-foreground',
                info: 'tw-border-info-border tw-bg-info-surface tw-text-info',
                success: 'tw-border-success-border tw-bg-success-surface tw-text-success',
                warning: 'tw-border-warning-border tw-bg-warning-surface tw-text-warning',
                destructive: 'tw-border-destructive-border tw-bg-destructive-surface tw-text-destructive'
            }
        },
        defaultVariants: {
            size: 'medium',
            tone: 'neutral'
        }
    }
);

const Badge = React.forwardRef(({ className: _className, style: _style, label, size, tone, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(badgeVariants({ size, tone }))}
        {...props}
    >
        <Text tone="inherit" variant="caption">{label}</Text>
    </div>
));

Badge.displayName = 'Badge';

export { Badge };
